import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { getConsent, saveConsent } from '../lib/metrics';

const CATEGORIES = [
  {
    key: 'necessary',
    label: 'Necessary',
    description: 'Technical cookies and local storage required for basic site functionality like theme preference, consent storage, and keeping you signed in to the community forum. These cannot be disabled.',
    alwaysOn: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Help us understand how visitors use our site so we can improve it. We use Google Analytics 4 and PostHog. No personal data is collected.',
  },
  {
    key: 'functional',
    label: 'Functional',
    description: 'Enables optional features like your read-later list, achievement tracking, and command palette preferences.',
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'We do not currently use marketing or advertising cookies. This option is included for future compliance and transparency.',
  },
];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState('banner');
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    if (getConsent() === null) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    function handleReopen() {
      const current = getConsent();
      setPrefs({
        necessary: true,
        analytics: current ? current.analytics : false,
        functional: current ? current.functional : false,
        marketing: current ? current.marketing : false,
      });
      setStage('settings');
      setVisible(true);
    }
    window.addEventListener('reopen-cookie-settings', handleReopen);
    return () => window.removeEventListener('reopen-cookie-settings', handleReopen);
  }, []);

  function openSettings() {
    const current = getConsent();
    setPrefs({
      necessary: true,
      analytics: current ? current.analytics : false,
      functional: current ? current.functional : false,
      marketing: current ? current.marketing : false,
    });
    setStage('settings');
  }

  function handleAcceptAll() {
    saveConsent({ analytics: true, functional: true, marketing: true });
    setVisible(false);
  }

  function handleRejectAll() {
    saveConsent({ analytics: false, functional: false, marketing: false });
    setVisible(false);
  }

  function handleSave() {
    saveConsent({
      analytics: prefs.analytics,
      functional: prefs.functional,
      marketing: prefs.marketing,
    });
    setVisible(false);
  }

  function togglePref(key) {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  }

  if (!visible) return null;

  return (
    <>
      {stage === 'banner' && (
        <div className="cookie-overlay">
          <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
            <div className="cookie-banner-inner">
              <div className="cookie-banner-icon">
                <Cookie size={18} />
              </div>
              <div className="cookie-banner-text">
                <p>
                  We use cookies to improve your experience. Some are essential, others help us understand how you use the site. The community forum also stores a local session to keep you signed in.{' '}
                  <Link to="/privacy">Privacy policy</Link> · <Link to="/terms">Terms of Service</Link>
                </p>
              </div>
              <div className="cookie-banner-actions">
                <button className="cookie-btn cookie-btn-ghost" onClick={handleRejectAll}>
                  Reject All
                </button>
                <button className="cookie-btn cookie-btn-secondary" onClick={openSettings}>
                  Customize
                </button>
                <button className="cookie-btn cookie-btn-primary" onClick={handleAcceptAll}>
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {stage === 'settings' && (
        <div className="cookie-overlay cookie-overlay-modal" role="dialog" aria-label="Cookie settings">
          <div className="cookie-modal">
            <div className="cookie-modal-header">
              <div className="cookie-modal-header-left">
                <div className="cookie-modal-icon-wrap">
                  <Cookie size={20} />
                </div>
                <div>
                  <span className="cookie-modal-title">Cookie Settings</span>
                  <span className="cookie-modal-subtitle">Manage your cookie preferences</span>
                </div>
              </div>
              <button className="cookie-modal-close" onClick={() => setVisible(false)} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="cookie-modal-body">
              {CATEGORIES.map(cat => (
                <div key={cat.key} className={`cookie-category ${cat.alwaysOn ? 'cookie-category-disabled' : ''}`}>
                  <div className="cookie-category-info">
                    <span className="cookie-category-label">
                      {cat.label}
                      {cat.alwaysOn && <span className="cookie-category-badge">Always</span>}
                    </span>
                    <p className="cookie-category-desc">{cat.description}</p>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cat.alwaysOn || prefs[cat.key]}
                      disabled={cat.alwaysOn}
                      onChange={() => togglePref(cat.key)}
                    />
                    <span className="cookie-toggle-track">
                      <span className="cookie-toggle-thumb" />
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div className="cookie-modal-footer">
              <button className="cookie-btn cookie-btn-ghost" onClick={handleRejectAll}>
                Reject All
              </button>
              <button className="cookie-btn cookie-btn-secondary" onClick={handleAcceptAll}>
                Accept All
              </button>
              <button className="cookie-btn cookie-btn-primary" onClick={handleSave}>
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
